import classNames from 'classnames';
// eslint-disable-next-line no-restricted-imports
import NextLink, { type LinkProps } from 'next/link';
import { usePathname } from 'next/navigation';
import type { AnchorHTMLAttributes, PropsWithChildren } from 'react';

import { useViewParameters } from '@/contexts/navigation';
import { serialize } from '@/contexts/navigation/serialization';

export interface GeneralLinkProps
  extends PropsWithChildren<LinkProps>,
    Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  disabled?: boolean;
}

export const Link = (props: GeneralLinkProps) => {
  const { disabled, children } = props;
  return (
    <NextLink
      {...props}
      className={classNames(props.className ? props.className : '', {
        'pointer-events-none': disabled,
      })}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : undefined}
      prefetch={false}
    >
      {children}
    </NextLink>
  );
};

type NavigationLinkProps = Omit<GeneralLinkProps, 'href'> & {
  path: string;
  searchParams?: Record<string, unknown>;
};

export const NavigationLink = (props: NavigationLinkProps) => {
  const { path, searchParams = {}, ...rest } = props;
  const { getAll, setParams } = useViewParameters();
  const currentPath = usePathname();
  const hasSearchParams = Object.keys(searchParams).length > 0;

  let url = path;
  if (hasSearchParams) {
    const { pageIndex, ...currentParams } = getAll();

    const params =
      path === currentPath
        ? {
            ...Object.entries(currentParams).reduce<Record<string, unknown>>(
              (acc, [key, value]) => {
                acc[key] = key === 'pageIndex' ? 0 : value;
                return acc;
              },
              {}
            ),
            ...searchParams,
          }
        : searchParams;
    url += `?${new URLSearchParams(serialize(params))}`;
  }

  return (
    <Link
      {...rest}
      href={url}
      onClick={() => {
        if (hasSearchParams && path === currentPath) {
          setParams((state) => ({
            ...state,
            ...searchParams,
          }));
        }
      }}
    />
  );
};
